import { CATEGORY_PROFILES, createCategorySelection, getCategoryProfile, getDefaultCategoryProfile } from "../category-profiles";
import { CONTRACT_OPTION_CATALOG, DEFAULT_FORM_CONFIG, getConsultationProductNameOverride, getContractOptionsForContext, getDefaultContractTypeForContext, getDefaultUpsellRateForContext, type FormConfigVersion } from "../form-config";
import type { DoctorProfile, IntakeCategoryId, IntakeData, IntakeDomain, ProcedureDetail, ProductOption } from "../types";
import { GENERAL_GUIDANCE_DEFAULTS } from "./guidance-defaults";

// This legacy key preserves same-origin drafts; the new domain cannot read old-origin localStorage.
export const LOCAL_STORAGE_KEY = "meet-the-world:draft:v1";
export const FIXED_UPSELL_COMMISSION_RATE = "10";
export const DEPOSIT_CONTRACT_TYPES = ["deposit", "deposit_upsell"] as const;
export const UPSELL_CONTRACT_TYPES = ["free_reservation_upsell", "deposit_upsell"] as const;

export function isDepositContractType(value: string): boolean {
  return (DEPOSIT_CONTRACT_TYPES as readonly string[]).includes(value);
}

export function isUpsellContractType(value: string): boolean {
  return (UPSELL_CONTRACT_TYPES as readonly string[]).includes(value);
}

// 초대(어드민 오퍼) 단위 계약 옵션 제공 범위. 빈 값 = 카테고리 기본 옵션 전체 제공.
export function parseOfferedContractTypes(value: unknown): string[] {
  const known = new Set(Object.values(CONTRACT_OPTION_CATALOG).map((option) => option.value));
  const seen = new Set<string>();
  return String(value ?? "")
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter((item) => {
      if (!item || !known.has(item) || seen.has(item)) return false;
      seen.add(item);
      return true;
    });
}

export function sanitizeUpsellRate(value: unknown): string {
  const text = String(value ?? "").trim().replace(/%$/, "");
  if (!/^\d{1,3}(\.\d{1,2})?$/.test(text)) return "";
  const rate = Number(text);
  return rate > 0 && rate <= 100 ? String(rate) : "";
}

export function normalizeContractOfferForCategory(offeredTypes: string[], categoryId: IntakeCategoryId, domain: string, config: FormConfigVersion = DEFAULT_FORM_CONFIG): string[] {
  const allowed = new Set(getContractOptionsForContext({ categoryId, domain: domain as IntakeDomain }, config).options.map((option) => option.value));
  return offeredTypes.filter((value) => allowed.has(value));
}

export function createDraftId(): string {
  const random = Math.random().toString(36).slice(2, 10);
  return `mtw_${Date.now().toString(36)}_${random}`;
}

export function createProductOption(): ProductOption {
  return {
    optionName: "",
    listPrice: "",
    salePrice: "",
    depositOrSettlementPrice: "",
    freeReservationLabel: "무료예약",
    depositAmount: "",
    settlementPrice: "",
    maxQuantityPerSlot: "",
    duration: "",
    includedItems: "",
    excludedItems: "",
    recommendationReason: "",
    detailDescription: "",
    effectDuration: "",
    recommendedTargets: "",
    productPrecautions: "",
  };
}

export function createDefaultConsultationProduct(): ProductOption {
  return {
    optionName: "무료예약 | 상담 후 시술 결정",
    listPrice: "0",
    salePrice: "0",
    depositOrSettlementPrice: "무료예약",
    freeReservationLabel: "무료예약",
    depositAmount: "",
    settlementPrice: "",
    maxQuantityPerSlot: "",
    duration: "상담 후 결정",
    includedItems: "현장 상담 후 고객에게 적합한 시술을 결정합니다.",
    excludedItems: "",
    recommendationReason: "외국인 고객은 어떤 시술을 받을지 정하지 못한 경우가 많아, 상담 후 결정 상품을 유지하는 것을 추천합니다.",
    detailDescription: GENERAL_GUIDANCE_DEFAULTS.consultationProduct.detailDescription,
    effectDuration: "",
    recommendedTargets: "",
    productPrecautions: "",
  };
}

// 상담 상품 옵션명은 문항 설정에서 카테고리별로 바꿀 수 있고, 빈 값이면 상담 상품을 넣지 않는다.
export function resolveConsultationProductName(categoryId: IntakeCategoryId, config: FormConfigVersion = DEFAULT_FORM_CONFIG): string {
  const profile = getCategoryProfile(categoryId) ?? getDefaultCategoryProfile();
  const override = getConsultationProductNameOverride({ categoryId, domain: profile.domain }, config);
  return (override ?? profile.consultationProduct.optionName).trim();
}

// 카테고리마다 상담 상품 문구가 다를 수 있어, 설정된 문구로 채워진 행도 자동 입력 시드로 인정한다.
function createConfiguredConsultationSeeds(categoryIds: IntakeCategoryId[], config: FormConfigVersion): ProductOption[] {
  return Array.from(new Set(categoryIds.filter(Boolean))).flatMap((categoryId) => {
    const optionName = resolveConsultationProductName(categoryId, config);
    return optionName ? [createCategoryConsultationProduct(categoryId, optionName)] : [];
  });
}

function createCategoryConsultationProduct(categoryId: IntakeCategoryId, optionName?: string): ProductOption {
  const profile = getCategoryProfile(categoryId) ?? getDefaultCategoryProfile();
  return {
    ...createProductOption(),
    optionName: optionName ?? profile.consultationProduct.optionName,
    listPrice: "0",
    salePrice: "0",
    depositOrSettlementPrice: "무료예약",
    freeReservationLabel: "무료예약",
    duration: profile.consultationProduct.duration,
    includedItems: profile.consultationProduct.includedItems,
    recommendationReason: profile.consultationProduct.recommendationReason,
    detailDescription: GENERAL_GUIDANCE_DEFAULTS.consultationProduct.detailDescription,
  };
}

const knownConsultationOptionNames = new Set(CATEGORY_PROFILES.map((profile) => profile.consultationProduct.optionName));

const productOptionKeys: Array<keyof ProductOption> = [
  "optionName",
  "listPrice",
  "salePrice",
  "depositOrSettlementPrice",
  "freeReservationLabel",
  "depositAmount",
  "settlementPrice",
  "maxQuantityPerSlot",
  "duration",
  "includedItems",
  "excludedItems",
  "recommendationReason",
  "detailDescription",
  "effectDuration",
  "recommendedTargets",
  "productPrecautions",
];

function shouldReplaceDefaultText(value: unknown, fallback: string, ...knownDefaults: string[]): boolean {
  const text = String(value ?? "").trim();
  return !text || text === fallback || knownDefaults.includes(text);
}

function productValue(product: ProductOption, key: keyof ProductOption): string {
  return String(product[key] ?? "").trim();
}

function createKnownConsultationProducts(): ProductOption[] {
  return [createDefaultConsultationProduct(), ...CATEGORY_PROFILES.map((profile) => createCategoryConsultationProduct(profile.id))];
}

function isBlankProductRow(product: ProductOption): boolean {
  return productOptionKeys.every((key) => {
    if (key === "freeReservationLabel") return productValue(product, key) === "" || productValue(product, key) === "무료예약";
    return productValue(product, key) === "";
  });
}

function isUntouchedConsultationProduct(product: ProductOption, extraCandidates: ProductOption[]): boolean {
  return [...createKnownConsultationProducts(), ...extraCandidates].some((knownProduct) => (
    productOptionKeys.every((key) => productValue(product, key) === productValue(knownProduct, key))
  ));
}

function hasKnownConsultationName(product: ProductOption, extraNames: string[]): boolean {
  const optionName = product.optionName.trim();
  return knownConsultationOptionNames.has(optionName) || extraNames.includes(optionName);
}

function mergeCategoryConsultationProduct(product: ProductOption, categoryId: IntakeCategoryId, optionName: string, configuredSeeds: ProductOption[]): ProductOption {
  const nextDefault = createCategoryConsultationProduct(categoryId, optionName);
  const knownDefaults = [...createKnownConsultationProducts(), ...configuredSeeds, nextDefault];
  return productOptionKeys.reduce((nextProduct, key) => {
    const currentValue = productValue(product, key);
    const isDefaultValue = knownDefaults.some((knownProduct) => productValue(knownProduct, key) === currentValue);
    return {
      ...nextProduct,
      [key]: !currentValue || isDefaultValue ? nextDefault[key] : product[key],
    };
  }, product);
}

function splitMultiValue(value: unknown): string[] {
  return String(value ?? "")
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function filterMultiValueToOptions(value: unknown, options: Array<{ value: string }>): string {
  const allowed = new Set(options.map((option) => option.value));
  return splitMultiValue(value).filter((item) => allowed.has(item)).join("\n");
}

function getCategoryContractTypeDefault(categoryId: IntakeCategoryId, domain: string, config: FormConfigVersion = DEFAULT_FORM_CONFIG): string {
  return getDefaultContractTypeForContext(categoryId, domain, config);
}

export function normalizeContractTypeForCategory(contractType: string, categoryId: IntakeCategoryId, domain: string, config: FormConfigVersion = DEFAULT_FORM_CONFIG): string {
  const allowedValues = new Set(getContractOptionsForContext({ categoryId, domain: domain as IntakeDomain }, config).options.map((option) => option.value));
  if (allowedValues.has(contractType)) return contractType;
  const legacyRemap = domain === "medical" && contractType === "deposit"
    ? "deposit_upsell"
    : domain !== "medical" && contractType === "deposit_upsell"
      ? "deposit"
      : null;
  if (legacyRemap && allowedValues.has(legacyRemap)) return legacyRemap;
  return getCategoryContractTypeDefault(categoryId, domain, config);
}

export function applyCategoryPreset(data: IntakeData, categoryId: IntakeCategoryId, config: FormConfigVersion = DEFAULT_FORM_CONFIG): IntakeData {
  const profile = getCategoryProfile(categoryId);
  if (!profile) return { ...data, category: createCategorySelection("") };

  const defaults = createDefaultIntakeData();
  const previousProfile = getCategoryProfile(data.category?.categoryId);
  const content = {
    ...data.content,
    sellingPointTags: filterMultiValueToOptions(data.content.sellingPointTags, profile.sellingPointOptions),
    targetCustomers: filterMultiValueToOptions(data.content.targetCustomers, profile.targetCustomerOptions),
  };
  const previousContractDefault = previousProfile
    ? getCategoryContractTypeDefault(previousProfile.id, previousProfile.domain, config)
    : defaults.contract.contractType;
  const normalizedContractType = normalizeContractTypeForCategory(
    shouldReplaceDefaultText(data.contract.contractType, defaults.contract.contractType, previousContractDefault)
      ? getCategoryContractTypeDefault(categoryId, profile.domain, config)
      : data.contract.contractType,
    categoryId,
    profile.domain,
    config,
  );
  const previousOfferedTypes = parseOfferedContractTypes(data.contract.offeredContractTypes);
  const compatibleOfferedTypes = normalizeContractOfferForCategory(previousOfferedTypes, categoryId, profile.domain, config);
  // 명시적 오퍼가 새 카테고리와 호환되지 않아도 빈 값(전체 제공)으로 넓히지 않는다.
  const nextOfferedTypes = previousOfferedTypes.length
    ? (compatibleOfferedTypes.length ? compatibleOfferedTypes : [normalizedContractType])
    : [];
  const nextContractType = nextOfferedTypes.length && !nextOfferedTypes.includes(normalizedContractType)
    ? nextOfferedTypes[0]
    : normalizedContractType;

  // 0원 상담 상품은 업셀링 계약에서만 의미가 있고, 문항 설정에서 카테고리별로 끄거나 문구를 바꿈 수 있다.
  const consultationProductName = resolveConsultationProductName(categoryId, config);
  const offersConsultationProduct = isUpsellContractType(nextContractType) && consultationProductName !== "";
  const configuredSeeds = createConfiguredConsultationSeeds([categoryId, previousProfile?.id ?? ""], config);
  const nextProducts = [...data.products];
  if (!nextProducts.length || isBlankProductRow(nextProducts[0]) || isUntouchedConsultationProduct(nextProducts[0], configuredSeeds)) {
    nextProducts[0] = offersConsultationProduct ? createCategoryConsultationProduct(categoryId, consultationProductName) : createProductOption();
    while (nextProducts.length < 5) nextProducts.push(createProductOption());
  } else if (offersConsultationProduct && hasKnownConsultationName(nextProducts[0], configuredSeeds.map((seed) => seed.optionName))) {
    nextProducts[0] = mergeCategoryConsultationProduct(nextProducts[0], categoryId, consultationProductName, configuredSeeds);
  }

  // 수수료율 기본값: 초대 오퍼 → 카테고리 기본값(문항 설정 rule) → 10%. 파트너가 직접 기입한 값은 유지한다.
  const offeredRate = sanitizeUpsellRate(data.contract.offeredUpsellCommissionRate);
  const previousRateDefault = previousProfile ? sanitizeUpsellRate(getDefaultUpsellRateForContext({ categoryId: previousProfile.id, domain: previousProfile.domain }, config)) : "";
  const nextRateDefault = sanitizeUpsellRate(getDefaultUpsellRateForContext({ categoryId, domain: profile.domain }, config));
  const targetRate = offeredRate || nextRateDefault || FIXED_UPSELL_COMMISSION_RATE;
  const currentRate = String(data.contract.upsellCommissionRate ?? "").trim();
  const rateReplaceable = !currentRate || currentRate === FIXED_UPSELL_COMMISSION_RATE || currentRate === previousRateDefault || currentRate === offeredRate;

  return {
    ...data,
    category: createCategorySelection(categoryId),
    content,
    contract: {
      ...data.contract,
      contractType: nextContractType,
      offeredContractTypes: nextOfferedTypes.join("\n"),
      upsellCommissionRate: isUpsellContractType(nextContractType) && rateReplaceable ? targetRate : data.contract.upsellCommissionRate,
    },
    reservation: {
      ...data.reservation,
      reservationUnit: shouldReplaceDefaultText(data.reservation.reservationUnit, defaults.reservation.reservationUnit, previousProfile?.reservationUnitDefault ?? "") ? (profile.reservationUnitDefault ?? defaults.reservation.reservationUnit) : data.reservation.reservationUnit,
    },
    policy: {
      ...data.policy,
      visitFlow: shouldReplaceDefaultText(data.policy.visitFlow, defaults.policy.visitFlow, getDefaultCategoryProfile().visitFlowDefault, previousProfile?.visitFlowDefault ?? "") ? profile.visitFlowDefault : data.policy.visitFlow,
      bookingQuestionChecklist: shouldReplaceDefaultText(data.policy.bookingQuestionChecklist, defaults.policy.bookingQuestionChecklist, getDefaultCategoryProfile().bookingQuestionChecklistDefault, previousProfile?.bookingQuestionChecklistDefault ?? "") ? filterMultiValueToOptions(profile.bookingQuestionChecklistDefault, profile.bookingQuestionOptions) : filterMultiValueToOptions(data.policy.bookingQuestionChecklist, profile.bookingQuestionOptions),
      healthRestrictions: shouldReplaceDefaultText(data.policy.healthRestrictions, defaults.policy.healthRestrictions, getDefaultCategoryProfile().healthRestrictionsDefault, previousProfile?.healthRestrictionsDefault ?? "") ? filterMultiValueToOptions(profile.healthRestrictionsDefault, profile.healthRestrictionOptions) : filterMultiValueToOptions(data.policy.healthRestrictions, profile.healthRestrictionOptions),
    },
    products: nextProducts,
  };
}

export function createDoctorProfile(): DoctorProfile {
  return {
    name: "",
    role: "",
    profileText: "",
    referenceUrl: "",
  };
}

export function createProcedureDetail(): ProcedureDetail {
  return {
    treatmentName: "",
    description: "",
    duration: "",
    effectDuration: "",
  };
}

export function createDefaultIntakeData(): IntakeData {
  return {
    category: createCategorySelection(""),
    company: {
      storeNameKo: "",
      storeNameEn: "",
      storeNameJa: "",
      storeNameZh: "",
      address: "",
      representativePhone: "",
      kakaoMapsUrl: "",
      kakaoCategory: "",
      kakaoPlaceName: "",
      kakaoPlaceId: "",
      kakaoRoadAddress: "",
      kakaoJibunAddress: "",
      kakaoCategoryGroupCode: "",
      kakaoCategoryGroupName: "",
      kakaoLongitude: "",
      kakaoLatitude: "",
      kakaoDistanceMeters: "",
      googleMapsUrl: "",
      websiteUrl: "",
      instagramUrl: "",
      contentReferenceUrl: "",
      contactName: "",
      contactRole: "",
      contactDepartment: "",
      contactEmail: "",
      contactPhone: "",
      useContactInfoForTaxInvoice: true,
      taxInvoiceContactName: "",
      taxInvoiceContactPhone: "",
      taxInvoiceContactEmail: "",
    },
    reservation: {
      useContactEmailForNotifications: true,
      useContactPhoneForAlimtalk: true,
      notificationEmails: "",
      notificationPhones: "",
      bookableStartOffsetDays: "2",
      bookableWindowMonths: "2",
      businessHours: "월~토 09:00~20:00 / 점심시간 12:00~13:00",
      reservationHours: "월~토 09:00~11:30 / 13:00~19:30",
      reservationUnit: "1시간",
      confirmationMode: "approval_required",
      communicationChannel: "email",
      // Admin 계정 문항은 기본 숨김이므로 기본값도 미신청으로 둔다.
      adminAccountNeeded: false,
      adminLoginId: "",
      adminPassword: "",
    },
    contract: {
      contractType: "free_reservation_upsell",
      upsellCommissionRate: FIXED_UPSELL_COMMISSION_RATE,
      offeredContractTypes: "",
      offeredUpsellCommissionRate: "",
      depositPolicyMemo: "",
      settlementMemo: "",
    },
    content: {
      catchphrase: GENERAL_GUIDANCE_DEFAULTS.content.catchphrase,
      sellingPointTags: "",
      touristConvenienceOptions: "",
      primarySellingPoint: GENERAL_GUIDANCE_DEFAULTS.content.primarySellingPoint,
      targetCustomers: "",
      targetCustomerConcernDetail: "",
      heroImageMemo: "",
      recommendationReason: GENERAL_GUIDANCE_DEFAULTS.content.recommendationReason,
      creatripExclusiveBenefitStatus: "있음",
      creatripExclusiveBenefit: GENERAL_GUIDANCE_DEFAULTS.content.creatripExclusiveBenefit,
      reviewBenefit: "",
      clinicStrengths: GENERAL_GUIDANCE_DEFAULTS.content.clinicStrengths,
      serviceEnvironment: GENERAL_GUIDANCE_DEFAULTS.content.serviceEnvironment,
      referenceLinks: "",
      googleReviewIntegration: "yes",
      googleBookingIntegration: "yes",
    },
    language: {
      availableLanguages: "",
      serviceScope: "",
      cautionLanguages: "",
      interpreterAvailability: "",
    },
    policy: {
      cancellationRefundPolicy: "방문 3일 전까지 예약 취소 시 100% 환불\n방문 2일 전부터 예약 취소 시 환불 불가\n예약시간에 10분 이상 늦을 경우 예약은 자동으로 취소되며, 이 경우 결제금액은 환불되지 않습니다.",
      visitFlow: "1. Creatrip 예약확인증과 여권을 데스크에 제시합니다.\n2. 로비 대기공간에서 대기합니다.\n3. 상담실에서 시술 전 과정과 소요시간을 안내받습니다.\n4. 원장님과 맞춤 상담을 진행합니다.\n5. 안내에 따라 시술을 받습니다.\n6. 시술 후 주의사항을 안내받습니다.",
      bookingQuestionChecklist: "예약자 여권 영문 이름\n생년월일\n성별\n국적\nSNS 채널\nSNS ID\n상담/시술 희망 내용\n개선 원하는 부위와 증상\n복용약·질환·임신 여부",
      bookingQuestions: "",
      otherPrecautions: GENERAL_GUIDANCE_DEFAULTS.policy.otherPrecautions,
      consultationFeePolicy: "not_charged",
      consultationFeeAmount: "",
      minorPolicy: "보호자 동의 또는 동반 필요",
      passportNeeded: "방문 시 여권 또는 외국인등록증 지참 필요",
      healthRestrictions: "임산부 사전 상담 필요\n특정 질환자 사전 상담 필요\n복용약이 있으면 사전 고지 필요",
    },
    documents: {
      imageUsageConsent: false,
      documentAccuracyConsent: false,
    },
    products: [createDefaultConsultationProduct(), ...Array.from({ length: 4 }, () => createProductOption())],
    doctors: [createDoctorProfile()],
    procedures: [createProcedureDetail()],
  };
}
